import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { ReactNode, useContext } from 'react';

import { RankingType } from '@flipster/apis/v2/trading-competition/types';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { OrdinalNumber } from '@flipster/react/design-utils/ordinal-number';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { useAuth } from '../../../../shared/auth/state';
import { RegisterButton } from '../../component/tc-button';
import { toRoiPercent } from '../../misc/roi';
import { CompetitionIdContext, competitionTabsBunja, myCompetitionInfoBunja } from '../../state';
import { competitionRankingsBunja } from './state';

const RankingWithLabel = ({ label, value, userId }: { label: ReactNode; value?: number; userId: number }) => {
  const id = useContext(CompetitionIdContext);
  const { rankingsPaginationAtom, onOffsetChangeAtom } = useBunja(competitionRankingsBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const rankingsPagination = useAtomValue(rankingsPaginationAtom);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const onOffsetChange = useSetAtom(onOffsetChangeAtom);
  const { isMobile } = useBreakpoint();

  return (
    <span className={hstack({ gap: 10 })}>
      <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>{label}</span>
      {value ? (
        <button
          type='button'
          className={hstack({ gap: 4 })}
          onClick={() => {
            segment.track(CompetitionsEvent.CompetitionTradingMyRankClick, { competition_id: id });
            const limit = rankingsPagination.limit;
            const targetOffset = Math.floor((value - 1) / limit) * limit;
            onOffsetChange(targetOffset);
            requestAnimationFrame(() => {
              const selector = `#leaderboard-table-${isMobile ? `mobile-` : ''}${selectedEventTabValue} [data-user-id="${userId}"]`;
              const el = document.querySelector(selector);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            });
          }}
        >
          <OrdinalNumber value={value} css={{ textStyle: 'Title/XS/Monospace', color: 'content.default.level-5' }} />
          <ChevronRightIcon size={12} color={token('colors.content.default.level-2')} />
        </button>
      ) : (
        <span className={css({ textStyle: 'Title/XS', color: 'content.default.level-5' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Unranked' />
        </span>
      )}
    </span>
  );
};

export const MyRank = () => {
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const { myCompetitionInfoDataAtom } = useBunja(myCompetitionInfoBunja);
  const { user } = useAuth();

  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const myCompetitionInfo = useAtomValue(myCompetitionInfoDataAtom);

  const isVolume = selectedEventTabValue === 'volume';
  const isRoi = selectedEventTabValue === 'roi';

  if (!user || !myCompetitionInfo) return null;

  const info = myCompetitionInfo[selectedEventTabValue as RankingType];
  if (!info) return null;

  return (
    <section
      className={flex({
        flexDir: { base: 'column', tablet: 'row' },
        justify: 'space-between',
        align: 'center',
        p: 24,
        rounded: { base: 12, tablet: 16 },
        bg: 'background.default.level-1',
        gap: { base: 24, tablet: 0 },
      })}
    >
      <div className={vstack({ alignItems: 'flex-start', gap: { base: 16, tablet: 12 }, tabletDown: { w: '100%' } })}>
        <p
          className={css({
            textStyle: 'Title/M',
            color: 'content.default.level-5',
          })}
        >
          {user.nickname}
        </p>
        <div className={flex({ gap: { base: 12, tablet: 32 }, flexDir: { base: 'column', tablet: 'row' } })}>
          <RankingWithLabel
            label={
              isVolume ? (
                <FormattedMessage id='Competition.r251215.My_volume_rank' />
              ) : isRoi ? (
                <FormattedMessage id='Tmp.inseok.My_roi_rank' />
              ) : (
                <FormattedMessage id='Competition.r251215.My_pnl_rank' />
              )
            }
            value={info.rank}
            userId={user.userId}
          />
          <span className={hstack({ gap: 10 })}>
            <span
              className={css({
                textStyle: 'Text/M/Regular',
                color: 'content.default.level-2',
              })}
            >
              {isVolume ? (
                <FormattedMessage id='Competition.r251215.My_volume_symbol' values={{ symbol: UsdtCurrency }} />
              ) : isRoi ? (
                <FormattedMessage id='Tmp.inseok.My_roi_symbol' values={{ symbol: '%' }} />
              ) : (
                <FormattedMessage id='Competition.r251215.My_pnl_symbol' values={{ symbol: UsdtCurrency }} />
              )}
            </span>
            <Formatted.Number
              value={isRoi ? toRoiPercent(info.value) : info.value}
              precision={2}
              withColor
              withPlus
              css={{ textStyle: 'Title/XS/Monospace', color: 'content.default.level-5' }}
            />
          </span>
        </div>
      </div>
      <RegisterButton buttonLocation='my_rank' />
    </section>
  );
};
